/**
 * BusinessLinesTab - Manage the CMS-driven Business Lines + Sub Types tree.
 *
 * Surfaces a flat list of every Business Line (active and inactive) with
 * its Sub Types nested underneath. Each row supports inline rename,
 * active toggle, and delete (system rows can't be deleted — the server
 * enforces this, and the UI hides the button to match).
 *
 * The tree backs the Work Order / Inspection / Location pickers, so
 * changes here propagate everywhere those forms render. Disabling a
 * line/sub-type hides it from new selections but keeps existing
 * records resolvable by slug.
 *
 * Edit UX mirrors PhotoTypesTab: inline rename buffers that commit
 * on Enter / blur. Lines expand to show their sub-types; the body
 * of each expanded line carries the sub-type CRUD.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Alert, Checkbox } from '../../components/atoms';
import {
	businessLineService,
	BusinessLine,
	BusinessLineSubType,
} from '../../services/businessLineService';
import { qk } from '../../lib/queryKeys';

const BusinessLinesTab: React.FC = () => {
	const queryClient = useQueryClient();

	/* Inline create form — a single text input + Add button. New lines
	 * default to is_active=true; admins can flip the toggle afterward. */
	const [newLineLabel, setNewLineLabel] = useState('');

	/* Track which lines are expanded so the sub-type list renders. */
	const [expanded, setExpanded] = useState<Set<number>>(new Set());

	/* Per-row rename buffers, keyed by "line:<id>" or "sub:<id>". The
	 * buffer holds the in-flight string; commit happens on Enter / blur. */
	const [renameDraft, setRenameDraft] = useState<Record<string, string>>({});

	/* Per-line buffer for "add sub-type" inline forms. */
	const [newSubLabel, setNewSubLabel] = useState<Record<number, string>>({});

	/* List query — includes inactive lines (list(false)) so the CMS shows
	 * every row. Keyed distinctly from the active-only variant other pages use. */
	const linesQuery = useQuery({
		queryKey: qk.businessLines.list({ active: false }),
		queryFn: async () => {
			// The BusinessLine/BusinessLineSubType TS types and the rest of
			// the app (JobForm, etc.) consume this endpoint as snake_case.
			// Don't camelize here — doing so renamed is_active/is_system/
			// sub_types to camelCase keys that the JSX below never read,
			// which made newly-added sub-types appear to vanish on save.
			const res = await businessLineService.list(false);
			const payload = res.data as { data?: BusinessLine[] };
			return payload.data ?? [];
		},
	});

	const lines: BusinessLine[] = useMemo(() => linesQuery.data ?? [], [linesQuery.data]);
	const isLoading = linesQuery.isLoading;
	const error = linesQuery.error ? (linesQuery.error as Error).message : null;

	/* ---------------------------------------------------------------
	 * Mutations
	 *
	 * Every business-line / sub-type change invalidates the whole
	 * businessLines cache (qk.businessLines.all) so BOTH this tab's
	 * inactive-inclusive list AND the active-only variant consumed by the
	 * WO/Inspection/Location pickers refetch. The tree backs those forms,
	 * so they must stay in sync after a CMS edit.
	 * --------------------------------------------------------------- */
	const invalidateLines = () => queryClient.invalidateQueries({ queryKey: qk.businessLines.all });

	const createLineMutation = useMutation({
		mutationFn: (label: string) => businessLineService.create({ label }),
		onSuccess: invalidateLines,
	});
	const updateLineMutation = useMutation({
		mutationFn: (vars: { id: number; patch: { label?: string; is_active?: boolean } }) => businessLineService.update(vars.id, vars.patch),
		onSuccess: invalidateLines,
	});
	const deleteLineMutation = useMutation({
		mutationFn: (id: number) => businessLineService.delete(id),
		onSuccess: invalidateLines,
	});
	const createSubMutation = useMutation({
		mutationFn: (vars: { lineId: number; label: string }) => businessLineService.createSubType(vars.lineId, { label: vars.label }),
		onSuccess: invalidateLines,
	});
	const updateSubMutation = useMutation({
		mutationFn: (vars: { id: number; patch: { label?: string; is_active?: boolean } }) => businessLineService.updateSubType(vars.id, vars.patch),
		onSuccess: invalidateLines,
	});
	const deleteSubMutation = useMutation({
		mutationFn: (id: number) => businessLineService.deleteSubType(id),
		onSuccess: invalidateLines,
	});

	const handleCreateLine = async () => {
		const label = newLineLabel.trim();
		if (!label) return;
		try {
			await createLineMutation.mutateAsync(label);
			setNewLineLabel('');
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to create business line');
		}
	};

	const handleRenameLine = async (line: BusinessLine) => {
		const key = `line:${line.id}`;
		const next = (renameDraft[key] ?? line.label).trim();
		if (!next || next === line.label) {
			setRenameDraft((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
			return;
		}
		try {
			await updateLineMutation.mutateAsync({ id: line.id, patch: { label: next } });
			setRenameDraft((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to rename');
		}
	};

	const handleToggleLineActive = async (line: BusinessLine) => {
		try {
			await updateLineMutation.mutateAsync({ id: line.id, patch: { is_active: !line.is_active } });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to toggle');
		}
	};

	const handleDeleteLine = async (line: BusinessLine) => {
		if (!window.confirm(`Delete business line "${line.label}"? This will also delete all of its sub-types. Existing records that reference this line will keep their slug but the label won't resolve.`)) return;
		try {
			await deleteLineMutation.mutateAsync(line.id);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to delete');
		}
	};

	const handleCreateSubType = async (line: BusinessLine) => {
		const label = (newSubLabel[line.id] ?? '').trim();
		if (!label) return;
		try {
			await createSubMutation.mutateAsync({ lineId: line.id, label });
			setNewSubLabel((prev) => ({ ...prev, [line.id]: '' }));
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to create sub-type');
		}
	};

	const handleRenameSubType = async (sub: BusinessLineSubType) => {
		const key = `sub:${sub.id}`;
		const next = (renameDraft[key] ?? sub.label).trim();
		if (!next || next === sub.label) {
			setRenameDraft((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
			return;
		}
		try {
			await updateSubMutation.mutateAsync({ id: sub.id, patch: { label: next } });
			setRenameDraft((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to rename sub-type');
		}
	};

	const handleToggleSubActive = async (sub: BusinessLineSubType) => {
		try {
			await updateSubMutation.mutateAsync({ id: sub.id, patch: { is_active: !sub.is_active } });
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to toggle');
		}
	};

	const handleDeleteSubType = async (sub: BusinessLineSubType) => {
		if (!window.confirm(`Delete sub-type "${sub.label}"? Existing records that reference this sub-type will keep their slug.`)) return;
		try {
			await deleteSubMutation.mutateAsync(sub.id);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to delete');
		}
	};

	const toggleExpanded = (id: number) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	/* ---------------------------------------------------------------
	 * Render
	 * --------------------------------------------------------------- */

	return (
		<div className="business-lines-tab" data-ref="organism-business-lines-tab">
			<p className="text-14px text-brand-text-muted mb-16px">
				Manage the Business Line + Sub Type taxonomy that drives the Work Order, Inspection, and Location pickers. Lines flagged as "System" are seeded and can be renamed / disabled but not deleted.
			</p>

			{/* Inline create */}
			<div className="flex items-end gap-8px mb-24px">
				<div className="flex-1">
					<label htmlFor="newLineLabel" className="block text-12px font-medium text-brand-text mb-4px">New Business Line</label>
					<input
						id="newLineLabel"
						type="text"
						value={newLineLabel}
						onChange={(e) => setNewLineLabel(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') handleCreateLine(); }}
						placeholder="e.g. Snow Removal"
						className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
					/>
				</div>
				<Button variant="primary" onClick={handleCreateLine} disabled={!newLineLabel.trim()}>Add Line</Button>
			</div>

			{error && (
				<Alert variant="error" className="mb-16px">{error}</Alert>
			)}

			{isLoading ? (
				<div className="text-14px text-brand-text-muted py-16px">Loading…</div>
			) : lines.length === 0 ? (
				<div className="text-14px text-brand-text-muted py-16px">No business lines yet.</div>
			) : (
				<div className="space-y-12px">
					{lines.map((line) => {
						const renameKey = `line:${line.id}`;
						const draft = renameDraft[renameKey];
						const isExpanded = expanded.has(line.id);
						return (
							<div key={line.id} className={['border rounded-md', line.is_active ? 'border-brand-border bg-white' : 'border-brand-border bg-brand-surface/40'].join(' ')}>
								<div className="flex flex-wrap items-center gap-8px px-16px py-12px">
									<button type="button" onClick={() => toggleExpanded(line.id)} className="inline-flex items-center justify-center w-44px h-44px shrink-0 -ml-12px rounded-4px text-12px text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface transition-colors" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
										{isExpanded ? '▾' : '▸'}
									</button>

									<input
										type="text"
										value={draft ?? line.label}
										onChange={(e) => setRenameDraft((prev) => ({ ...prev, [renameKey]: e.target.value }))}
										onBlur={() => handleRenameLine(line)}
										onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
										className="w-full tablet:flex-1 min-w-[140px] px-8px py-4px border border-transparent hover:border-brand-border focus:border-brand-primary rounded-md text-16px desktop:text-14px font-medium text-brand-text focus:outline-none focus:ring-0"
									/>

									<span className="hidden tablet:inline text-12px text-brand-text-muted font-mono">{line.slug}</span>

									{line.is_system && (
										<span className="px-8px py-2px rounded-full text-12px font-medium bg-amber-100 text-amber-800 border border-amber-200">System</span>
									)}

									<Checkbox checked={line.is_active} onChange={() => handleToggleLineActive(line)} label="Active" className="business-lines-tab__active items-center" labelClassName="text-12px text-brand-text-muted" dataRef="organism-business-lines-tab-active" />

									{!line.is_system && (
										<button
											type="button"
											onClick={() => handleDeleteLine(line)}
											className="text-12px text-red-600 hover:text-red-800 px-8px py-8px min-h-44px inline-flex items-center"
										>
											Delete
										</button>
									)}
								</div>

								{isExpanded && (
									<div className="border-t border-brand-border bg-brand-surface/30 px-16px py-12px">
										{/* Sub-type list */}
										{(line.sub_types ?? []).length === 0 ? (
											<p className="text-12px text-brand-text-muted mb-8px">No sub-types under this line yet.</p>
										) : (
											<div className="space-y-4px mb-12px">
												{(line.sub_types ?? []).map((sub) => {
													const subKey = `sub:${sub.id}`;
													const subDraft = renameDraft[subKey];
													return (
														<div key={sub.id} className="flex flex-wrap items-center gap-8px">
															<input
																type="text"
																value={subDraft ?? sub.label}
																onChange={(e) => setRenameDraft((prev) => ({ ...prev, [subKey]: e.target.value }))}
																onBlur={() => handleRenameSubType(sub)}
																onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
																className="w-full tablet:flex-1 min-w-[140px] px-8px py-4px border border-transparent hover:border-brand-border focus:border-brand-primary rounded-md text-16px desktop:text-13px text-brand-text focus:outline-none focus:ring-0 bg-white"
															/>
															<span className="hidden tablet:inline text-11px text-brand-text-muted font-mono">{sub.slug}</span>
															{sub.is_system && (
																<span className="px-6px py-1px rounded-full text-11px font-medium bg-amber-100 text-amber-800 border border-amber-200">System</span>
															)}
															<Checkbox checked={sub.is_active} onChange={() => handleToggleSubActive(sub)} label="Active" className="business-lines-tab__sub-active items-center" labelClassName="text-11px text-brand-text-muted" dataRef="organism-business-lines-tab-sub-active" />
															{!sub.is_system && (
																<button type="button" onClick={() => handleDeleteSubType(sub)} className="text-11px text-red-600 hover:text-red-800 px-6px py-8px min-h-44px inline-flex items-center">Delete</button>
															)}
														</div>
													);
												})}
											</div>
										)}

										{/* Inline create sub-type */}
										<div className="flex items-center gap-8px">
											<input
												type="text"
												value={newSubLabel[line.id] ?? ''}
												onChange={(e) => setNewSubLabel((prev) => ({ ...prev, [line.id]: e.target.value }))}
												onKeyDown={(e) => { if (e.key === 'Enter') handleCreateSubType(line); }}
												placeholder="New sub-type label"
												className="flex-1 px-8px py-4px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white"
											/>
											<Button variant="secondary" onClick={() => handleCreateSubType(line)} disabled={!(newSubLabel[line.id] ?? '').trim()}>Add Sub Type</Button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default BusinessLinesTab;
